import React, { ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';

import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import ContextoDessem from 'components/Relatorios/Dessem/secoes/shared/ContextoDessem';
import SemInforme from 'components/Relatorios/Dessem/secoes/shared/SecaoDessemEstado/SemInforme';

import { ResponseError } from 'services/api/base';
import { DessemSecaoParams } from 'types/Relatorios/Dessem';

/**
 * Subconjunto do resultado de um `useGetDessem*Query` que decide o estado da seção.
 * `currentData` (nunca `data`): o `data` do RTK Query guarda o último resultado bem-sucedido
 * de QUALQUER argumento — numa troca de dia/cenário cujo backend responde 404, ele continua
 * sendo o informe do dia anterior, e `isLoading` só vale na primeira carga.
 */
export interface ResultadoSecaoDessem<T> {
  currentData?: T;
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => unknown;
}

export interface SecaoDessemEstadoProps<T> {
  resultado: ResultadoSecaoDessem<T>;
  params: DessemSecaoParams;
  /** Só é chamado com o dado do argumento atual (dia/cenário pedidos). */
  children: (data: T) => ReactNode;
}

const SEM_INFORME_REGEX = /n[ãa]o h[áa] informe/i;

/**
 * Dia/cenário sem estudo. O backend responde HTTP 200 com `code: 404`, `message:
 * 'Não encontrado'` e `description: 'Não há informe para esta data e cenário.'`; o baseQuery
 * normaliza em `{ status: 404, data: message, description }`. O status é o contrato; o texto é
 * a rede de segurança para um 404 que chegue com outro code.
 */
export const isErroSemInforme = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const { status, data, description } = error as Partial<ResponseError>;
  if (status === 404) return true;
  return [data, description].some((texto) => (
    typeof texto === 'string' && SEM_INFORME_REGEX.test(texto)
  ));
};

/**
 * Estado compartilhado das seis seções do Informe DESSEM, a partir do resultado do hook do
 * RTK Query (`currentData`, `isFetching`, `isError`, `error`, `refetch`):
 * - dado do argumento atual presente -> renderiza a seção (`children(data)`); com `isFetching`
 *   junto (refetch do mesmo dia), continua mostrando o dado em vez de piscar;
 * - buscando sem dado do argumento atual (primeira carga OU troca de dia/cenário) -> spinner;
 * - erro 404 ("Não há informe") -> estado vazio
 *   "Sem informe de <D> (previsão para <D+1>) · <cenário>";
 * - outro erro (402/403/500/rede) -> `QueryErrorState` com "Tentar novamente" (`refetch`);
 * - nada disso (consulta `skip`ada) -> nada.
 *
 * Com dado e no erro, a faixa de contexto (`ContextoDessem`: data por extenso · cenário
 * aplicado) vem acima — o usuário enxerga de que dia/cenário é a seção mesmo com o painel
 * recolhido. Não no spinner, nem no "Sem informe" (cujo título já traz data · cenário).
 */
export default function SecaoDessemEstado<T>({
  resultado, params, children,
}: SecaoDessemEstadoProps<T>) {
  const {
    currentData, isFetching, isError, error, refetch,
  } = resultado;

  if (currentData !== undefined) {
    return (
      <>
        <ContextoDessem params={params} />
        {children(currentData)}
      </>
    );
  }

  if (isFetching) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (isError) {
    if (isErroSemInforme(error)) return <SemInforme params={params} />;
    return (
      <>
        <ContextoDessem params={params} />
        <QueryErrorState title="Não foi possível carregar a seção" onRetry={refetch} />
      </>
    );
  }

  return null;
}
